import React, { useState, useEffect } from 'react';
import { 
  collection, 
  getDocs, 
  doc, 
  getDoc,
  Firestore 
} from 'firebase/firestore';
import { httpsCallable, Functions } from 'firebase/functions';

interface CollectionData {
  id: string;
  permissions: Record<string, string | string[]>;
  fields: Array<{
    id: string;
    name: string;
    lastUpdated: number;
    enabled?: boolean;
    permissions?: Record<string, string | string[]>;
  }>;
  lastUpdated?: number;
  schema?: Array<{
    key: string;
    label: string;
    type: 'freeform' | 'enum';
    required: boolean;
    defaultValue?: string;
    options?: string[];
  }>;
}

interface PermissionsData {
  collectionLevel?: Record<string, string[]>;
  documentLevel?: Record<string, Record<string, string[]>>;
  lastUpdated?: number;
}

interface CollectionManagerProps {
  db: Firestore;
  functions: Functions;
  setError: (error: string) => void;
  setSuccess: (success: string) => void;
  loading: boolean;
  setLoading: (loading: boolean) => void;
}

const CollectionManager: React.FC<CollectionManagerProps> = ({
  db,
  functions,
  setError,
  setSuccess,
  loading,
  setLoading
}) => {
  // System-reserved keys that cannot be used for schema fields
  const systemKeys = ['name', 'created', 'enabled', 'lastUpdated', 'id'];

  const [collections, setCollections] = useState<CollectionData[]>([]);
  const [selectedCollection, setSelectedCollection] = useState<CollectionData | null>(null);
  const [permissionsData, setPermissionsData] = useState<PermissionsData | null>(null);
  const [activeTab, setActiveTab] = useState<'permissions' | 'schema'>('permissions');
  const [showSchemaForm, setShowSchemaForm] = useState(false);
  const [schemaForm, setSchemaForm] = useState({
    key: '',
    label: '',
    type: 'freeform' as 'freeform' | 'enum',
    defaultValue: '',
    enumOptions: [''],
    required: false
  });
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailModalContext, setEmailModalContext] = useState({
    role: '',
    level: 'collection' as 'collection' | 'document',
    collectionName: '',
    documentId: '',
    documentName: ''
  });
  const [emailInput, setEmailInput] = useState('');
  const [emailTags, setEmailTags] = useState<string[]>([]);
  const [showRoleForm, setShowRoleForm] = useState(false);
  const [newCollectionName, setNewCollectionName] = useState('');
  const [newRoleName, setNewRoleName] = useState('');
  const [showCreateForm, setShowCreateForm] = useState(false);

  // Get all available roles from collection permissions, ensuring read and write are always included
  const getAvailableRoles = () => {
    if (!selectedCollection) return ['read', 'write'];
    const existingRoles = Object.keys(selectedCollection.permissions || {});
    const allRoles = new Set(['read', 'write', ...existingRoles]);
    return Array.from(allRoles).sort();
  };

  // Open email modal for specific target
  const openEmailModal = (level: 'collection' | 'document', role: string, docId?: string, docName?: string) => {
    setEmailModalContext({
      role,
      level,
      collectionName: selectedCollection?.id || '',
      documentId: docId || '',
      documentName: docName || ''
    });
    setEmailInput('');
    setEmailTags([]);
    setShowEmailModal(true);
  };

  // Parse emails from input and convert to tags
  const parseEmailsToTags = (input: string) => {
    const emails = input
      .split(/[,\n\s]+/)
      .map(email => email.trim())
      .filter(email => email.length > 0 && email.includes('@'))
      .filter(email => !emailTags.includes(email)); // Only new emails
    
    if (emails.length > 0) {
      setEmailTags(prev => [...prev, ...emails]); // Add to existing tags
    }
    setEmailInput('');
  };

  // Handle email input changes
  const handleEmailInputChange = (value: string) => {
    setEmailInput(value);
    
    // Auto-convert to tags when comma, space, or newline is entered
    if (value.includes(',') || value.includes('\n') || value.includes(' ')) {
      const newEmails = value
        .split(/[,\n\s]+/)
        .map(email => email.trim())
        .filter(email => email.length > 0 && email.includes('@'))
        .filter(email => !emailTags.includes(email)); // Only emails not already in tags
      
      if (newEmails.length > 0) {
        setEmailTags(prev => [...prev, ...newEmails]); // Add to existing tags
        setEmailInput('');
      } else {
        // If no valid new emails, just clear the input
        setEmailInput('');
      }
    }
  };

  // Remove email tag
  const removeEmailTag = (emailToRemove: string) => {
    setEmailTags(prev => prev.filter(email => email !== emailToRemove));
  };

  // Grant access to all emails in tags
  const handleGrantAccess = async () => {
    if (emailTags.length === 0) {
      setError('Please enter at least one email address');
      return;
    }

    try {
      setLoading(true);
      const setGroupPermissions = httpsCallable(functions, 'setGroupPermissions');
      
      const payload: any = {
        collectionName: emailModalContext.collectionName,
        level: emailModalContext.level,
        role: emailModalContext.role,
        emails: emailTags,
        operation: 'grant'
      };

      if (emailModalContext.level === 'document' && emailModalContext.documentId) {
        payload.docId = emailModalContext.documentId;
      }

      await setGroupPermissions(payload);

      const target = emailModalContext.level === 'document' 
        ? `document ${emailModalContext.documentName}` 
        : 'collection';
      setSuccess(`Access granted to ${emailTags.length} user(s) for ${emailModalContext.role} on ${target}`);
      
      // Reset and close modal
      setEmailInput('');
      setEmailTags([]);
      setShowEmailModal(false);
      
      if (selectedCollection) {
        await loadPermissions(selectedCollection.id);
      }
    } catch (error: any) {
      console.error('Error granting access:', error);
      setError(error.message || 'Failed to grant access');
    } finally {
      setLoading(false);
    }
  };

  // Helper to ensure APP_ADMIN is always included in permission arrays
  const ensureAppAdminIncluded = (value: string | string[]): string | string[] => {
    if (value === 'ALLOW_ALL' || value === 'ALLOW_NONE') {
      return value;
    }
    
    if (value === 'ALLOW_SPECIFIED') {
      return ['APP_ADMIN', 'ALLOW_SPECIFIED']; // Both APP_ADMIN role and ALLOW_SPECIFIED flag
    }
    
    const roles = Array.isArray(value) ? value : [value];
    if (!roles.includes('APP_ADMIN')) {
      return ['APP_ADMIN', ...roles];
    }
    return value;
  };

  // Format permission value for display (hide APP_ADMIN)
  const formatPermissionForDisplay = (value: string | string[]): string => {
    if (value === 'ALLOW_ALL' || value === 'ALLOW_NONE' || value === 'ALLOW_SPECIFIED') {
      return value;
    }
    
    const roles = Array.isArray(value) ? value : [value];
    const filteredRoles = roles.filter(role => role !== 'APP_ADMIN');
    return filteredRoles.length > 0 ? filteredRoles.join(', ') : 'APP_ADMIN only';
  };

  // Parse permission input and add APP_ADMIN
  const parsePermissionInput = (input: string): string | string[] => {
    const trimmed = input.trim();
    if (trimmed === 'ALLOW_ALL' || trimmed === 'ALLOW_NONE' || trimmed === 'ALLOW_SPECIFIED') {
      return trimmed;
    }
    
    if (trimmed.includes(',')) {
      const roles = trimmed.split(',').map(s => s.trim()).filter(s => s.length > 0);
      return ensureAppAdminIncluded(roles) as string[];
    }
    
    return ensureAppAdminIncluded(trimmed) as string[];
  };

  const getPermissionLevel = (value: string | string[]): string => {
    if (value === 'ALLOW_ALL' || value === 'ALLOW_NONE' || value === 'ALLOW_SPECIFIED') {
      return value;
    }
    return 'ALLOW_SPECIFIED';
  };

  const canManagePermissions = (permissionLevel: string): boolean => {
    return permissionLevel === 'ALLOW_SPECIFIED';
  };

  // Load collections from metadata
  const loadCollections = async () => {
    try {
      setLoading(true);
      const metadataCollection = collection(db, 'metadata');
      const snapshot = await getDocs(metadataCollection);
      
      const collectionsData: CollectionData[] = [];
      snapshot.forEach((doc) => {
        collectionsData.push({
          id: doc.id,
          ...doc.data()
        } as CollectionData);
      });
      
      setCollections(collectionsData.sort((a, b) => a.id.localeCompare(b.id)));
    } catch (error) {
      console.error('Error loading collections:', error);
      setError('Failed to load collections');
    } finally {
      setLoading(false);
    }
  };

  // Load permissions for selected collection
  const loadPermissions = async (collectionName: string) => {
    try {
      const permissionsDoc = doc(db, 'permissions', collectionName);
      const snapshot = await getDoc(permissionsDoc);
      
      if (snapshot.exists()) {
        setPermissionsData(snapshot.data() as PermissionsData);
      } else {
        setPermissionsData({ collectionLevel: {}, documentLevel: {} });
      }
    } catch (error) {
      console.error('Error loading permissions:', error);
      setError('Failed to load permissions');
      setPermissionsData(null);
    }
  };

  useEffect(() => {
    loadCollections();
  }, [db]);

  useEffect(() => {
    if (selectedCollection) {
      loadPermissions(selectedCollection.id);
    } else {
      setPermissionsData(null);
    }
  }, [selectedCollection]);

  // Create new collection with default permissions
  const handleCreateCollection = async () => {
    if (!newCollectionName.trim()) {
      setError('Collection name is required');
      return;
    }

    try {
      setLoading(true);
      const createCollection = httpsCallable(functions, 'createCollection');
      
      // Default permissions with APP_ADMIN and ALLOW_SPECIFIED
      const permissions = {
        read: ['APP_ADMIN', 'ALLOW_SPECIFIED'],
        write: ['APP_ADMIN', 'ALLOW_SPECIFIED']
      };

      await createCollection({
        name: newCollectionName.trim(),
        permissions
      });

      setSuccess(`Collection '${newCollectionName}' created successfully`);
      setNewCollectionName('');
      setShowCreateForm(false);
      await loadCollections();
    } catch (error: any) {
      console.error('Error creating collection:', error);
      setError(error.message || 'Failed to create collection');
    } finally {
      setLoading(false);
    }
  };

  // Add new role to collection
  const handleAddRole = async () => {
    if (!selectedCollection || !newRoleName.trim()) {
      setError('Role name is required');
      return;
    }

    const roleName = newRoleName.trim();
    if (selectedCollection.permissions[roleName]) {
      setError('Role already exists');
      return;
    }

    try {
      setLoading(true);
      const setCollectionMetadataPermissions = httpsCallable(functions, 'setCollectionMetadataPermissions');
      
      const updatedPermissions = {
        ...selectedCollection.permissions,
        [roleName]: ['APP_ADMIN', 'ALLOW_SPECIFIED'] // New roles start with APP_ADMIN + ALLOW_SPECIFIED
      };

      await setCollectionMetadataPermissions({
        name: selectedCollection.id,
        metadataPolicy: updatedPermissions
      });

      setSuccess(`Role '${roleName}' added successfully`);
      setNewRoleName('');
      setShowRoleForm(false);
      await loadCollections();
      
      // Update selected collection
      const updatedCollection = { ...selectedCollection, permissions: updatedPermissions };
      setSelectedCollection(updatedCollection);
    } catch (error: any) {
      console.error('Error adding role:', error);
      setError(error.message || 'Failed to add role');
    } finally {
      setLoading(false);
    }
  };

  // Update collection permission level
  const handlePermissionLevelChange = async (role: string, newLevel: string) => {
    if (!selectedCollection) return;

    try {
      setLoading(true);
      const setCollectionMetadataPermissions = httpsCallable(functions, 'setCollectionMetadataPermissions');
      
      let permissionValue: string | string[];
      if (newLevel === 'ALLOW_ALL' || newLevel === 'ALLOW_NONE') {
        permissionValue = newLevel;
      } else if (newLevel === 'ALLOW_SPECIFIED') {
        // Always ensure APP_ADMIN is included when selecting ALLOW_SPECIFIED
        permissionValue = ensureAppAdminIncluded('ALLOW_SPECIFIED');
      } else {
        // For any other value, ensure APP_ADMIN is included
        permissionValue = ensureAppAdminIncluded(newLevel);
      }
      
      const updatedPermissions = {
        ...selectedCollection.permissions,
        [role]: permissionValue
      };

      await setCollectionMetadataPermissions({
        name: selectedCollection.id,
        metadataPolicy: updatedPermissions
      });

      setSuccess(`Permission level for '${role}' updated successfully`);
      await loadCollections();
      
      // Update selected collection
      const updatedCollection = { ...selectedCollection, permissions: updatedPermissions };
      setSelectedCollection(updatedCollection);
    } catch (error: any) {
      console.error('Error updating permission level:', error);
      setError(error.message || 'Failed to update permission level');
    } finally {
      setLoading(false);
    }
  };

  // Update document permission level
  const handleDocumentPermissionLevelChange = async (docId: string, role: string, newLevel: string) => {
    if (!selectedCollection) return;

    try {
      setLoading(true);
      const setDocumentMetadataPermissions = httpsCallable(functions, 'setDocumentMetadataPermissions');
      
      // Get current document permissions
      const field = selectedCollection.fields.find(f => f.id === docId);
      if (!field) {
        setError('Document not found');
        return;
      }

      let permissionValue: string | string[];
      if (newLevel === 'ALLOW_ALL' || newLevel === 'ALLOW_NONE') {
        permissionValue = newLevel;
      } else if (newLevel === 'ALLOW_SPECIFIED') {
        // Always ensure APP_ADMIN is included when selecting ALLOW_SPECIFIED
        permissionValue = ensureAppAdminIncluded('ALLOW_SPECIFIED');
      } else {
        // For any other value, ensure APP_ADMIN is included
        permissionValue = ensureAppAdminIncluded(newLevel);
      }
      
      const updatedDocumentPermissions = {
        ...field.permissions,
        [role]: permissionValue
      };

      // Ensure read and write roles always exist
      if (!updatedDocumentPermissions.read) {
        updatedDocumentPermissions.read = ensureAppAdminIncluded('ALLOW_SPECIFIED');
      }
      if (!updatedDocumentPermissions.write) {
        updatedDocumentPermissions.write = ensureAppAdminIncluded('ALLOW_SPECIFIED');
      }

      await setDocumentMetadataPermissions({
        collectionName: selectedCollection.id,
        docId: docId,
        metadataPolicy: updatedDocumentPermissions
      });

      setSuccess(`Permission level for '${role}' on document '${field.name}' updated successfully`);
      await loadCollections();
      
      // Update selected collection with new document permissions
      const updatedFields = selectedCollection.fields.map(f => 
        f.id === docId ? { ...f, permissions: updatedDocumentPermissions } : f
      );
      const updatedCollection = { ...selectedCollection, fields: updatedFields };
      setSelectedCollection(updatedCollection);
    } catch (error: any) {
      console.error('Error updating document permission level:', error);
      setError(error.message || 'Failed to update document permission level');
    } finally {
      setLoading(false);
    }
  };

  // Remove individual user permission
  const handleRemoveUser = async (email: string, role: string, level: 'collection' | 'document', docId?: string) => {
    if (!selectedCollection) return;

    try {
      setLoading(true);
      const setGroupPermissions = httpsCallable(functions, 'setGroupPermissions');
      
      const payload: any = {
        collectionName: selectedCollection.id,
        level,
        role,
        emails: [email],
        operation: 'revoke'
      };

      if (level === 'document' && docId) {
        payload.docId = docId;
      }

      await setGroupPermissions(payload);

      const target = level === 'document' ? `document ${docId}` : 'collection';
      setSuccess(`Permission revoked for ${email} on ${target}`);
      
      await loadPermissions(selectedCollection.id);
    } catch (error: any) {
      console.error('Error removing permission:', error);
      setError(error.message || 'Failed to remove permission');
    } finally {
      setLoading(false);
    }
  };

  // Validate schema field key
  const validateSchemaFieldKey = (key: string): string | null => {
    const trimmedKey = key.trim();
    
    if (!trimmedKey) {
      return 'Key is required';
    }
    
    if (systemKeys.includes(trimmedKey)) {
      return `'${trimmedKey}' is a system-reserved key and cannot be used. Reserved keys: ${systemKeys.join(', ')}`;
    }
    
    // Check if key already exists in current schema
    if (selectedCollection?.schema?.some(field => field.key === trimmedKey)) {
      return 'A field with this key already exists';
    }
    
    return null;
  };

  // Add schema field to collection
  const handleAddSchemaField = async () => {
    if (!selectedCollection || !schemaForm.key.trim() || !schemaForm.label.trim()) {
      setError('Key and label are required');
      return;
    }

    // Validate the key
    const keyValidationError = validateSchemaFieldKey(schemaForm.key);
    if (keyValidationError) {
      setError(keyValidationError);
      return;
    }

    // Validate enum options if type is enum
    if (schemaForm.type === 'enum') {
      const validOptions = schemaForm.enumOptions.filter(opt => opt.trim().length > 0);
      if (validOptions.length === 0) {
        setError('At least one enum option is required');
        return;
      }
    }

    try {
      setLoading(true);
      const updateCollectionMetadata = httpsCallable(functions, 'updateCollectionMetadata');
      
      // Create the schema field
      const newSchemaField = {
        key: schemaForm.key.trim(),
        label: schemaForm.label.trim(),
        type: schemaForm.type,
        required: schemaForm.required,
        ...(schemaForm.defaultValue && { defaultValue: schemaForm.defaultValue }),
        ...(schemaForm.type === 'enum' && { 
          options: schemaForm.enumOptions.filter(opt => opt.trim().length > 0) 
        })
      };

      // Get existing schema or create new one
      const currentSchema = selectedCollection.schema || [];
      const updatedSchema = [...currentSchema, newSchemaField];
      
      // Update the metadata with new schema using the new function
      const updatedMetadata = {
        permissions: selectedCollection.permissions,
        fields: selectedCollection.fields,
        schema: updatedSchema
      };

      await updateCollectionMetadata({
        name: selectedCollection.id,
        metadata: updatedMetadata
      });

      setSuccess(`Schema field '${newSchemaField.label}' added successfully`);
      
      // Reset form
      setSchemaForm({
        key: '',
        label: '',
        type: 'freeform',
        defaultValue: '',
        enumOptions: [''],
        required: false
      });
      setShowSchemaForm(false);
      
      // Reload collection to get updated schema
      await loadCollections();
      
      // Update selected collection with new schema
      const updatedCollection = { 
        ...selectedCollection, 
        schema: updatedSchema 
      };
      setSelectedCollection(updatedCollection);
      
    } catch (error: any) {
      console.error('Error adding schema field:', error);
      setError(error.message || 'Failed to add schema field');
    } finally {
      setLoading(false);
    }
  };

  // Remove schema field
  const handleRemoveSchemaField = async (fieldKey: string) => {
    if (!selectedCollection) return;

    try {
      setLoading(true);
      const updateCollectionMetadata = httpsCallable(functions, 'updateCollectionMetadata');
      
      const currentSchema = selectedCollection.schema || [];
      const updatedSchema = currentSchema.filter((field: any) => field.key !== fieldKey);
      
      // Update the entire metadata object correctly using the new function
      const updatedMetadata = {
        permissions: selectedCollection.permissions,
        fields: selectedCollection.fields,
        schema: updatedSchema
      };

      await updateCollectionMetadata({
        name: selectedCollection.id,
        metadata: updatedMetadata
      });

      setSuccess(`Schema field removed successfully`);
      
      // Reload and update
      await loadCollections();
      const updatedCollection = { 
        ...selectedCollection, 
        schema: updatedSchema 
      };
      setSelectedCollection(updatedCollection);
      
    } catch (error: any) {
      console.error('Error removing schema field:', error);
      setError(error.message || 'Failed to remove schema field');
    } finally {
      setLoading(false);
    }
  };

  // Handle enum option changes
  const handleEnumOptionChange = (index: number, value: string) => {
    const newOptions = [...schemaForm.enumOptions];
    newOptions[index] = value;
    setSchemaForm(prev => ({ ...prev, enumOptions: newOptions }));
  };

  const addEnumOption = () => {
    setSchemaForm(prev => ({ 
      ...prev, 
      enumOptions: [...prev.enumOptions, ''] 
    }));
  };

  const removeEnumOption = (index: number) => {
    if (schemaForm.enumOptions.length > 1) {
      const newOptions = schemaForm.enumOptions.filter((_, i) => i !== index);
      setSchemaForm(prev => ({ ...prev, enumOptions: newOptions }));
    }
  };

  return (
    <div className="unified-collection-manager">
      <div className="collection-sidebar">
        <h3>Collections</h3>
        <div className="collection-list">
          {collections.map((col) => (
            <div
              key={col.id}
              className={`collection-item ${selectedCollection?.id === col.id ? 'selected' : ''}`}
              onClick={() => setSelectedCollection(col)}
            >
              <div className="collection-name">{col.id}</div>
              <div className="collection-meta">
                {col.fields?.length || 0} documents • {Object.keys(col.permissions || {}).length} roles
              </div>
            </div>
          ))}
        </div>
        
        {!showCreateForm ? (
          <button 
            className="create-collection-btn"
            onClick={() => setShowCreateForm(true)}
            disabled={loading}
          >
            + Add Collection
          </button>
        ) : (
          <div className="create-collection-form">
            <input
              type="text"
              placeholder="Collection name"
              value={newCollectionName}
              onChange={(e) => setNewCollectionName(e.target.value)}
              className="collection-name-input"
            />
            
            <div className="form-actions">
              <button 
                onClick={handleCreateCollection}
                disabled={loading || !newCollectionName.trim()}
                className="create-btn"
              >
                Create
              </button>
              <button 
                onClick={() => {
                  setShowCreateForm(false);
                  setNewCollectionName('');
                }}
                disabled={loading}
                className="cancel-btn"
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>

      <div className="collection-details">
        {selectedCollection ? (
          <div>
            <div className="collection-header">
              <h3>{selectedCollection.id}</h3>
              <div className="header-actions">
                <button
                  onClick={() => setShowRoleForm(true)}
                  disabled={loading}
                  className="add-role-btn"
                >
                  + Add Role
                </button>
              </div>
            </div>

            <div className="collection-tabs">
              <button
                className={`tab-button ${activeTab === 'permissions' ? 'active' : ''}`}
                onClick={() => setActiveTab('permissions')}
              >
                Permissions
              </button>
              <button
                className={`tab-button ${activeTab === 'schema' ? 'active' : ''}`}
                onClick={() => setActiveTab('schema')}
              >
                Schema
              </button>
            </div>

            {showRoleForm && (
              <div className="role-form">
                <h4>Add New Role</h4>
                <div className="form-group">
                  <input
                    type="text"
                    placeholder="Role name (e.g., moderate, admin, custom_role)"
                    value={newRoleName}
                    onChange={(e) => setNewRoleName(e.target.value)}
                    className="role-name-input"
                  />
                </div>
                
                <div className="form-actions">
                  <button
                    onClick={handleAddRole}
                    disabled={loading || !newRoleName.trim()}
                    className="submit-btn"
                  >
                    Add Role
                  </button>
                  <button
                    onClick={() => {
                      setShowRoleForm(false);
                      setNewRoleName('');
                    }}
                    disabled={loading}
                    className="cancel-btn"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {showSchemaForm && (
              <div className="schema-form">
                <h4>Add Schema Field</h4>
                
                <div className="schema-form-grid">
                  <div className="form-group">
                    <label>Field Key:</label>
                    <input
                      type="text"
                      value={schemaForm.key}
                      onChange={(e) => setSchemaForm(prev => ({ ...prev, key: e.target.value }))}
                      placeholder="e.g., eventDate, description"
                      className="schema-input"
                    />
                    {schemaForm.key.trim() && systemKeys.includes(schemaForm.key.trim()) && (
                      <div className="validation-error">
                        '{schemaForm.key.trim()}' is a system-reserved key. Reserved keys: {systemKeys.join(', ')}
                      </div>
                    )}
                  </div>
                  
                  <div className="form-group">
                    <label>Field Label:</label>
                    <input
                      type="text"
                      value={schemaForm.label}
                      onChange={(e) => setSchemaForm(prev => ({ ...prev, label: e.target.value }))}
                      placeholder="e.g., Event Date, Description"
                      className="schema-input"
                    />
                  </div>
                  
                  <div className="form-group">
                    <label>Field Type:</label>
                    <select
                      value={schemaForm.type}
                      onChange={(e) => setSchemaForm(prev => ({ 
                        ...prev, 
                        type: e.target.value as 'freeform' | 'enum',
                        enumOptions: e.target.value === 'enum' ? [''] : prev.enumOptions
                      }))}
                      className="schema-select"
                    >
                      <option value="freeform">Freeform Text</option>
                      <option value="enum">Dropdown (Enum)</option>
                    </select>
                  </div>
                  
                  <div className="form-group">
                    <label>Default Value:</label>
                    <input
                      type="text"
                      value={schemaForm.defaultValue}
                      onChange={(e) => setSchemaForm(prev => ({ ...prev, defaultValue: e.target.value }))}
                      placeholder="Optional default value"
                      className="schema-input"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={schemaForm.required}
                      onChange={(e) => setSchemaForm(prev => ({ ...prev, required: e.target.checked }))}
                    />
                    Required Field
                  </label>
                </div>

                {schemaForm.type === 'enum' && (
                  <div className="enum-options-section">
                    <h5>Dropdown Options:</h5>
                    {schemaForm.enumOptions.map((option, index) => (
                      <div key={index} className="enum-option-row">
                        <input
                          type="text"
                          value={option}
                          onChange={(e) => handleEnumOptionChange(index, e.target.value)}
                          placeholder={`Option ${index + 1}`}
                          className="enum-option-input"
                        />
                        {schemaForm.enumOptions.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeEnumOption(index)}
                            className="remove-enum-option"
                          >
                            ×
                          </button>
                        )}
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={addEnumOption}
                      className="add-enum-option"
                    >
                      + Add Option
                    </button>
                  </div>
                )}
                
                <div className="form-actions">
                  <button
                    onClick={handleAddSchemaField}
                    disabled={loading}
                    className="submit-btn"
                  >
                    Add Field
                  </button>
                  <button
                    onClick={() => {
                      setShowSchemaForm(false);
                      setSchemaForm({
                        key: '',
                        label: '',
                        type: 'freeform',
                        defaultValue: '',
                        enumOptions: [''],
                        required: false
                      });
                    }}
                    disabled={loading}
                    className="cancel-btn"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {activeTab === 'permissions' && (
              <>
                <div className="collection-roles-section">
                  <h4>Collection Roles & Permission Levels</h4>
                  <div className="roles-grid">
                    {getAvailableRoles().map((role) => {
                      const value = selectedCollection.permissions?.[role] || ['APP_ADMIN', 'ALLOW_SPECIFIED'];
                      const permissionLevel = getPermissionLevel(value);
                      const canManage = canManagePermissions(permissionLevel);
                      return (
                        <div key={role} className="role-section">
                          <div className="role-header">
                            <h5>{role.charAt(0).toUpperCase() + role.slice(1)}</h5>
                            <div className="role-controls">
                              <select
                                value={permissionLevel}
                                onChange={(e) => handlePermissionLevelChange(role, e.target.value)}
                                disabled={loading}
                                className="permission-level-select"
                              >
                                <option value="ALLOW_ALL">Allow All Users</option>
                                <option value="ALLOW_SPECIFIED">Allow Specified Users</option>
                                <option value="ALLOW_NONE">Allow No Users</option>
                              </select>
                              {canManage && (
                                <button
                                  onClick={() => openEmailModal('collection', role)}
                                  disabled={loading}
                                  className="manage-users-btn"
                                >
                                  Manage Users
                                </button>
                              )}
                            </div>
                          </div>
                          
                          {canManage && (
                            <div className="role-users">
                              {permissionsData?.collectionLevel?.[role]?.length ? (
                                <div className="user-list">
                                  {permissionsData.collectionLevel[role]!.map((email) => (
                                    <div key={email} className="user-item">
                                      <span className="user-email">{email}</span>
                                      <button
                                        onClick={() => handleRemoveUser(email, role, 'collection')}
                                        disabled={loading}
                                        className="remove-user-btn"
                                        title="Remove permission"
                                      >
                                        ×
                                      </button>
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <div className="no-users">No users assigned to this role</div>
                              )}
                            </div>
                          )}
                          
                          {!canManage && (
                            <div className="permission-info">
                              {permissionLevel === 'ALLOW_ALL' && 'All users have this permission'}
                              {permissionLevel === 'ALLOW_NONE' && 'No users have this permission'}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div className="documents-overview">
                  <h4>Documents ({selectedCollection.fields?.length || 0})</h4>
                  <div className="documents-section">
                    {selectedCollection.fields?.map((field) => {
                      const docPermissions = permissionsData?.documentLevel?.[field.id];
                      return (
                        <div key={field.id} className="document-item">
                          <div className="document-header">
                            <span className="document-name">{field.name}</span>
                            <span className="document-id">({field.id})</span>
                            <span className={`document-status ${field.enabled ? 'enabled' : 'disabled'}`}>
                              {field.enabled ? 'Enabled' : 'Disabled'}
                            </span>
                          </div>

                          <div className="document-roles">
                            {getAvailableRoles().map(role => {
                              const fieldPermissionLevel = field.permissions?.[role] ? getPermissionLevel(field.permissions[role]) : 'ALLOW_SPECIFIED';
                              const hasSpecificUsers = Boolean(docPermissions?.[role]?.length);
                              const canManageDocRole = canManagePermissions(fieldPermissionLevel);
                              
                              return (
                                <div key={role} className="document-role">
                                  <div className="document-role-header">
                                    <span className="role-name">{role}</span>
                                    <div className="document-role-controls">
                                      <select
                                        value={fieldPermissionLevel}
                                        onChange={(e) => {
                                          handleDocumentPermissionLevelChange(field.id, role, e.target.value);
                                        }}
                                        disabled={loading}
                                        className="document-permission-select"
                                      >
                                        <option value="ALLOW_ALL">Allow All Users</option>
                                        <option value="ALLOW_SPECIFIED">Allow Specified Users</option>
                                        <option value="ALLOW_NONE">Allow No Users</option>
                                      </select>
                                      {canManageDocRole && (
                                        <button
                                          onClick={() => openEmailModal('document', role, field.id, field.name)}
                                          disabled={loading}
                                          className="manage-users-btn document-manage-btn"
                                        >
                                          Manage Users
                                        </button>
                                      )}
                                    </div>
                                  </div>
                                  
                                  {hasSpecificUsers && docPermissions?.[role] && (
                                    <div className="document-role-users">
                                      {docPermissions[role].map((email) => (
                                        <div key={email} className="doc-user-item">
                                          <span className="user-email">{email}</span>
                                          <button
                                            onClick={() => handleRemoveUser(email, role, 'document', field.id)}
                                            disabled={loading}
                                            className="remove-user-btn"
                                            title="Remove permission"
                                          >
                                            ×
                                          </button>
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                  
                                  {!canManageDocRole && (
                                    <div className="document-permission-info">
                                      {fieldPermissionLevel === 'ALLOW_ALL' && 'All users have this permission'}
                                      {fieldPermissionLevel === 'ALLOW_NONE' && 'No users have this permission'}
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                          
                          <div className="document-meta">
                            Last updated: {new Date(field.lastUpdated).toLocaleString()}
                          </div>
                        </div>
                      );
                    })}
                    
                    {(!selectedCollection.fields || selectedCollection.fields.length === 0) && (
                      <div className="empty-documents">
                        No documents in this collection yet.
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}

            {activeTab === 'schema' && (
              <div className="schema-section">
                <div className="schema-header">
                  <h4>Collection Schema</h4>
                  <button
                    onClick={() => setShowSchemaForm(true)}
                    disabled={loading}
                    className="add-schema-field-btn"
                  >
                    + Add Field
                  </button>
                </div>

                {selectedCollection.schema && selectedCollection.schema.length > 0 ? (
                  <div className="schema-fields-list">
                    {selectedCollection.schema.map((field: any, index: number) => (
                      <div key={field.key} className="schema-field-item">
                        <div className="schema-field-header">
                          <div className="schema-field-info">
                            <h5>{field.label}</h5>
                            <span className="schema-field-key">{field.key}</span>
                            {field.required && (
                              <span className="required-badge">Required</span>
                            )}
                          </div>
                          <button
                            onClick={() => handleRemoveSchemaField(field.key)}
                            disabled={loading}
                            className="remove-schema-field-btn"
                            title="Remove field"
                          >
                            ×
                          </button>
                        </div>
                        
                        <div className="schema-field-details">
                          <div className="schema-field-type">
                            Type: <span className="type-badge">{field.type}</span>
                          </div>
                          
                          {field.defaultValue && (
                            <div className="schema-field-default">
                              Default: <span className="default-value">{field.defaultValue}</span>
                            </div>
                          )}
                          
                          {field.type === 'enum' && field.options && (
                            <div className="schema-field-options">
                              Options: {field.options.map((option: string, i: number) => (
                                <span key={i} className="option-tag">{option}</span>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="no-schema-fields">
                    <h5>No schema fields defined</h5>
                    <p>Add fields to define the structure for documents in this collection.</p>
                    <button
                      onClick={() => setShowSchemaForm(true)}
                      disabled={loading}
                      className="add-first-schema-field-btn"
                    >
                      + Add First Field
                    </button>
                  </div>
                )}
              </div>
            )}

            {showEmailModal && (
              <div className="modal-overlay">
                <div className="email-modal">
                  <h4>
                    Grant {emailModalContext.role} access
                    {emailModalContext.level === 'collection' && ` to ${emailModalContext.collectionName}`}
                    {emailModalContext.level === 'document' && ` to ${emailModalContext.documentName}`}
                  </h4>
                  
                  <div className="email-input-section">
                    <label>Enter email addresses:</label>
                    <div className="email-tags-container">
                      {emailTags.length > 0 && (
                        <div className="email-tags-section">
                          {emailTags.map((email) => (
                            <div key={email} className="email-tag">
                              <span>{email}</span>
                              <button
                                type="button"
                                onClick={() => removeEmailTag(email)}
                                className="remove-tag-btn"
                              >
                                ×
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                      <div className="email-input-wrapper">
                        <input
                          type="text"
                          value={emailInput}
                          onChange={(e) => handleEmailInputChange(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ',') {
                              e.preventDefault();
                              if (emailInput.trim()) {
                                parseEmailsToTags(emailInput);
                              }
                            }
                            if (e.key === 'Backspace' && !emailInput && emailTags.length > 0) {
                              setEmailTags(prev => prev.slice(0, -1));
                            }
                          }}
                          onPaste={(e) => {
                            e.preventDefault();
                            const pastedText = e.clipboardData.getData('text');
                            parseEmailsToTags(pastedText);
                          }}
                          placeholder={emailTags.length === 0 ? "Enter email addresses (comma, space, or newline separated)" : "Add more emails..."}
                          className="email-input"
                        />
                      </div>
                    </div>
                    <div className="email-input-help">
                      Type or paste email addresses. They will automatically convert to tags.
                      {emailTags.length > 0 && ` (${emailTags.length} email${emailTags.length === 1 ? '' : 's'} added)`}
                    </div>
                  </div>
                  
                  <div className="modal-actions">
                    <button
                      onClick={handleGrantAccess}
                      disabled={loading || emailTags.length === 0}
                      className="grant-access-btn"
                    >
                      Grant Access ({emailTags.length} users)
                    </button>
                    <button
                      onClick={() => {
                        setShowEmailModal(false);
                        setEmailInput('');
                        setEmailTags([]);
                      }}
                      disabled={loading}
                      className="cancel-modal-btn"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="no-selection">
            <h3>Select a Collection</h3>
            <p>Choose a collection from the list to manage its roles, permissions, and documents.</p>
          </div>
        )}
      </div>

      {loading && (
        <div className="loading-overlay">
          <div className="loading-spinner"></div>
        </div>
      )}
    </div>
  );
};

export default CollectionManager;